import {createSelector} from 'reselect';
import {WeatherForecast} from '../../api/types';
import {Generic} from '../../utils';
import {
  getHash,
  getSelectedForecastModelByLocationHash,
} from '../location/selectors';
import {CommonRootState} from '../types';
import {
  getId,
  getSelectedForecastModelByGenericStationId,
} from '../weather/selectors';

const getWeatherRawHistoryData = (state: CommonRootState) => {
  return state.weatherData.historyData || {};
};

export const getWeatherHistoryData = createSelector(
  getWeatherRawHistoryData,
  (data) => new Generic.StationDataHandler(data),
);

export const EMPTY_FORECAST = {
  data: {
    daily: [],
    hourly: [],
  },
  metadata: {
    forecast_model: null,
    mesh_size: null,
    runs: [],
  },
  computed_at: null,
} as WeatherForecast;

export const getWeatherRawForecastData = (state: CommonRootState) => {
  return state.weatherData.forecastData || {};
};

export const getStationForecastData = createSelector(
  getWeatherRawForecastData,
  getSelectedForecastModelByGenericStationId,
  getId,
  (forecastDataDict, model, stationId) => {
    const key = model
      ? model.serverId.meshSize
        ? `${stationId}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
        : Object.keys(forecastDataDict).find((k) =>
            k.startsWith(`${stationId}-${model.serverId.forecastModel}-`),
          )
      : Object.keys(forecastDataDict).find((k) => k.startsWith(`${stationId}`));

    if (!key || !(key in forecastDataDict)) {
      return EMPTY_FORECAST;
    } else {
      return forecastDataDict[key];
    }
  },
);

export const getRawLocationForecastData = (state: CommonRootState) => {
  return state.weatherData.locationForecastData || {};
};

export const getLocationForecastData = createSelector(
  getRawLocationForecastData,
  getSelectedForecastModelByLocationHash,
  getHash,
  (forecastDataDict, model, locationHash) => {
    const key = model
      ? model.serverId.meshSize
        ? `${locationHash}-${model.serverId.forecastModel}-${model.serverId.meshSize}`
        : Object.keys(forecastDataDict).find((k) =>
            k.startsWith(`${locationHash}-${model.serverId.forecastModel}-`),
          )
      : Object.keys(forecastDataDict).find((k) =>
          k.startsWith(`${locationHash}`),
        );

    if (!key || !(key in forecastDataDict)) {
      return EMPTY_FORECAST;
    } else {
      return forecastDataDict[key];
    }
  },
);

export const getRawStationWeatherWarnings = (state: CommonRootState) => {
  return state.weatherData.stationWeatherWarnings || {};
};

export const getStationWeatherWarnings = createSelector(
  getRawStationWeatherWarnings,
  getId,
  (warningDataDict, stationId) => {
    return warningDataDict[stationId] || {warnings: [], computed_at: null};
  },
);

export const getRawLocationWeatherWarnings = (state: CommonRootState) => {
  return state.weatherData.locationWeatherWarnings || {};
};

export const getLocationWeatherWarnings = createSelector(
  getRawLocationWeatherWarnings,
  getHash,
  (warningDataDict, locationHash) => {
    return warningDataDict[locationHash] || {warnings: [], computed_at: null};
  },
);
